import { Rng, type ScreenContext } from '../../shared';
import { h } from '../../ui';
import { buildMapModel, type MapModel, type MapPlace, type MapPoint } from './mapModel';
import { roomArt, riverShape, distanceToSegment } from './mapArchitecture';
import type { Journey } from '../sim';
import './mapStyle.css';

const W = 720, H = 410;
// Alternate road stops are high on the page: their token sits below the place,
// while valley stops and crypt rooms use the empty space to the side.
const heroPosition = (m: MapModel, place: MapPlace): MapPoint => m.mode === 'road' && place.index % 2 === 1
  ? { x: place.x, y: place.y + 132 }
  : { x: place.x + (place.x > W - 115 ? -94 : 94), y: place.y + 58 };
const n = (value: number): string => value.toFixed(1);
const path = (d: string, fill = 'none', extra = ''): string => `<path d="${d}" fill="${fill}" ${extra}/>`;

function blob(rng: Rng, x: number, y: number, rx: number, ry: number): string {
  const points = Array.from({ length: 12 }, (_, i) => { const a = i / 12 * Math.PI * 2, r = .88 + rng.float() * .2; return [x + Math.cos(a) * rx * r, y + Math.sin(a) * ry * r]; });
  return `M${points.map(p => `${n(p[0])} ${n(p[1])}`).join('L')}Z`;
}

function tree(rng: Rng, x: number, y: number, scale: number): string {
  const green = rng.pick(['#84906a', '#a2a478', '#6e8065']);
  return `<g transform="translate(${n(x)} ${n(y)}) scale(${n(scale)})" stroke="#4f6046" stroke-width="1.4">`
    + path('M0 7L-1-14M-1-5L-9-14M-1-9L8-19', 'none')
    + path(blob(rng, -7, -19, 10, 9), green) + path(blob(rng, 7, -23, 10, 11), green) + path(blob(rng, 0, -32, 9, 10), green)
    + path('M-7-24l-3 4m14-10l4 3m-7-10l-1 4', 'none', 'opacity=".55"') + '</g>';
}

/** The crypt has its own architecture and props, while keeping the same saved encounters. */
function cryptPlace(id: string): string {
  const stone = '#b1b2a0', shadow = '#797d6a', water = '#809b95';
  let art = path('M-39 8V-40Q-39-68 0-76Q39-68 39-40V8', 'none', 'stroke="#969c83" stroke-width="2"')
    + path('M-39-28h9m-8-21l9 4m11-18l5 9M0-74v11m21-8l-5 9m18 12l-9 5m14 15h-9', 'none', 'stroke="#969c83" stroke-width="1.4"');
  switch(id) {
    case 'bridge':
      return art + path('M-38-9L-29-30 32-24 40 11 22 19-22 14Z', water, 'stroke="#698c88"')
        + path('M-31 4q9-5 18-1m12 8q13-4 24 0M-15-23l20 3', 'none', 'stroke="#d7dfc7"')
        + path('M-41-9Q-3-44 41-12V2H29Q24-23-2-21T-27 5H-41Z', stone)
        + path('M-38-10L-29-1m10-22l4 9M-1-28v9m18-6l-4 9m16-7l-7 9', 'none');
    case 'scribe':
      art += path('M-28 9V-55H27V9Z', '#a99570') + path('M-25-34h49M-25-13h49M-18-52v16m8-14v14m9-16v16m9-17v16m10-15v15M-19-29l4 13m7-15v16m8-15v16m10-16v16m9-15v14', 'none');
      return art + path('M-38 4l22-8L2 0 22-5 36 5 5 16-15 11Z', '#e7d9b1')
        + path('M1 0L5 15m-31-12l16 2m-12 2l13 2M23-5l11-22q12 6-4 16', 'none');
    case 'garden':
      art += path('M-38-4L-10-19 38-4 12 13Z', '#969573') + path('M-27 10l42 8M-16-11l28 8', 'none');
      for (const [x,y,r] of [[-21,-20,10],[3,-29,12],[24,-17,9],[-3,-5,8]]) art += path(`M${x} ${y+14}v-16`, 'none', 'stroke="#c1b895" stroke-width="5"')
        + path(`M${x-r} ${y}q${r/2}-${r*1.8} ${r} -${r*1.6}q${r/2} 0 ${r} ${r*1.6}Z`, '#a99e6f')
        + path(`M${x-4} ${y-3}h3m4-5h2`, 'none', 'stroke="#e2d8af" stroke-width="2"');
      return art + path('M-30-46q-5 10 5 16m45-32q9 13-2 22m-6-29v17', 'none', 'stroke="#7d8b65"');
    case 'knight':
      return art + path('M-8-54q0-19 17-19t15 19v14H-8Z', stone) + path('M-6-54H21M9-69v16M2-49h5m6 0h5', 'none')
        + path('M-15-37L0-45 21-41 31-25 17-20 16 4-8 6-11-20-22-18Z', '#9b9f8b')
        + path('M3-40v31M-6-6l-7 17m27-17l10 17M-28-41l-9 40m-7-22l16 3', 'none')
        + path('M21-15L42-19 44 4Q35 18 25 5Z', '#a99b62') + path('M32-15l2 23m-9-11l16-3', 'none', 'stroke="#945a48" stroke-width="3"');
    case 'chapel':
      return art + path('M-19-56Q0-77 18-55V-26H-19Z', shadow) + path('M0-64v29m-10-19h20', 'none', 'stroke="#c5b074" stroke-width="4"')
        + path('M-31-14H31L26 12H-25Z', stone) + path('M-36-20H36V-12H-36Z', '#c9c3a3')
        + path('M-27-23v-17m51 17v-15M-10-13v25M14-13v25', 'none')
        + path('M-27-42q-6-9 0-12q5 5 0 12M24-40q-5-9 0-12q5 5 0 12', '#c6a449')
        + path('M-12-25l14-3 14 4L2-20Z', '#e6d8af');
    case 'snail':
      return art + path('M-33 2H32L38 14H-38Z', stone)
        + path('M-26-4Q-12-13 12-11L24-19 32-7 24 0H-26Z', '#8c9980')
        + '<ellipse cx="-7" cy="-22" rx="20" ry="19" fill="#aaa88d"/>'
        + path('M-22-23Q-20-47 1-35T-2-8Q-19-5-19-25T-4-28Q4-20-7-18M23-16v-12m7 14l7-10M-25 7h50', 'none')
        + path('M-24-49l-3-9m15 4l3-8', 'none', 'stroke="#8a9774"');
    case 'vault':
      return art + path('M-28 11V-34Q0-60 28-34V11Z', '#8d907b')
        + path('M-21 7V-32Q0-50 20-32V7Z', '#636d60') + path('M-12-34V5M0-41V5M12-33V5M-21-17h42M-21-3h42', 'none')
        + path('M-8-16H8V-4H-8Z', '#b59e61') + path('M-32 13h65', 'none');
    case 'ferry':
      return art + path('M-41-20Q0-44 43-18L39 14Q0 33-39 11Z', '#aaa990')
        + path('M-37-19Q0-35 38-17L34 5Q0 19-34 3Z', water, 'stroke="#6b8b83"')
        + path('M-31-11q9-5 18-1m20 13q11 2 20-3', 'none', 'stroke="#d4ddc3"')
        + path('M-25-12Q-4-4 25-13L14 4-15 2Z', '#b0966f')
        + path('M-34-20l60 29M-36-21l10 6M6-11v-18m1-3v-4m1-3v-4', 'none');
  }
  return art;
}

/** Every vignette depicts one of the saved encounter types, rather than an unrelated decoration. */
function placeArt(p: MapPlace, crypt: boolean, rng: Rng): string {
  const ink = '#594832', stone = crypt ? '#b1b2a0' : '#d8c9a4', red = '#995445', gold = '#b89749';
  let art = '';
  switch (p.id) {
    case 'bridge':
      art = path('M-32-52C-4-44-32-21-4-6S17 24 7 38L28 42C42 12 14-5 11-18S32-46 1-53Z', crypt ? '#7c9690' : '#a8beb2', 'stroke="#7c9b96"')
        + path('M-25-34l15 3m24 33l12 4M-10 25l12 5M-10-47l12 4', 'none', 'stroke="#e5e3c8"')
        + path('M-39-9Q0-35 39-12L39 3Q0-19-39 6Z', '#c0a473')
        + path('M-39-15Q0-40 39-18M-38 7Q0-19 39 3M-33-19v22M-16-28v22M3-30v18M23-27v20M36-22v21', 'none')
        + path('M-30-8l3 8m10-14l3 8m10-11l2 7m11-8l2 6m10-4l2 5', 'none', 'stroke-width="1.3"');
      break;
    case 'scribe':
      art = path('M-20 7L-20-52 18-55 21 7Z', stone) + path('M-26-52L-3-78 25-55Z', red)
        + path('M-3-77v-13l16 4-16 4M-26 7h53M-12-42h8v12h-8Zm18-1h8v12H6Z', 'none')
        + path('M-6 7V-12Q1-23 9-12V7', '#695e49') + path('M-16-23h9m16 3h9m-30-34h9', 'none', 'stroke-width="1"')
        + path('M22-11l22-4 4 22-20 5Z', '#ece0bd') + path('M32-7l10-2m-9 7l10-2M35-17l11-23q9 7-6 17', 'none');
      break;
    case 'garden':
      art = path('M-42-13L21-41 49-9-17 20Z', '#aaa875', 'stroke="#7b8059"')
        + path('M-27-4l56-23M-15 7l56-23M-31-25v25M-11-34v24M15-44v21M33-32v18', 'none', 'stroke="#8c7952"');
      for (const [x, y] of [[-19,-14],[2,-23],[20,-22],[-4,-6],[20,-4],[35,-12]]) {
        art += `<circle cx="${x}" cy="${y}" r="8" fill="#7e9464" stroke="#536944"/>` + path(`M${x-4} ${y+2}q-3-7 3-8q6 0 4 7q-5 6-7 0`, 'none', 'stroke="#cbd0a0" stroke-width="1.2"');
      }
      break;
    case 'knight':
      art = path('M-35 8L-5-45 28 8Z', '#b56d57') + path('M-5-45v-25l23 6-23 6M-5-45L-1 8M-14 8l10-29 11 29M-39 9h72', 'none')
        + path('M24-30l25-1-2 27Q34 9 24-3Z', '#b69e50') + path('M36-28v25M26-17h20', 'none', 'stroke="#955242" stroke-width="4"')
        + path('M-34-16l-7-35m-5 14l14-4M-43-51l3-8 2 8', 'none');
      break;
    case 'chapel':
      art = path('M-32 9V-28L0-49 28-27V9Z', stone) + path('M-39-26L0-55 34-30', 'none', `stroke="${red}" stroke-width="6"`)
        + path('M-10-48V-71H10V-47M-14-71L0-83 14-71M0-83v-14m-6 5h12', 'none')
        + path('M-5-53v-10q5-7 10 0v10Z', gold) + path('M-9 9V-11a9 9 0 0118 0V9', '#665d4c')
        + `<circle cx="0" cy="-33" r="6" fill="#a0956a"/>` + path('M-30 13h57M-23-14h7m-7 10h7m39-8h7', 'none');
      break;
    case 'snail':
      art = path('M-36 8Q-17-9 11-4Q23-19 30-10L42 1 28 10Z', '#a9ad84')
        + `<ellipse cx="-6" cy="-17" rx="22" ry="21" fill="#c6ab72"/>`
        + path('M-18-15Q-23-39 0-31T6-8Q-10 4-17-12T-2-22Q7-19 0-14', 'none')
        + path('M24-10l-2-12m10 13l6-11M-38 13h76M-6-39l6-13 9 14', 'none')
        + `<circle cx="22" cy="-24" r="2.2" fill="${ink}"/><circle cx="39" cy="-21" r="2.2" fill="${ink}"/>`;
      break;
    case 'vault':
      art = path('M-35 11V-30Q0-74 34-30V11Z', stone) + path('M-24 11V-26Q0-54 23-26V11Z', '#786d59')
        + path('M-13 8V-24m12 31v-37m12 37v-31M-35-13h10m-6-20l9 6M-18-46l5 10M0-53v12m19-6l-6 10m16 5l-9 7m5 13h10M-43 12h85', 'none')
        + `<circle cx="1" cy="-12" r="5" fill="${gold}"/>` + path('M1-8v8', 'none');
      break;
    case 'ferry':
      art = path('M-49-34Q-24-51 1-34T53-27L50 20Q20 9-2 25T-53 12Z', crypt ? '#7c9690' : '#a8beb2', 'stroke="#7c9b96"')
        + path('M-44-23q10-5 19 0m43-8q13 5 23-1M-44 11q9-4 18 0m42 2q15 5 24-1', 'none', 'stroke="#ece4c5"')
        + path('M-26-6Q0 4 30-9L21 11H-17Z', '#b58b56') + path('M0-9V-52l27 31-27 1M-38-9l70 20M-42-12l11 6', 'none')
        + path('M2-47L24-23H2Z', '#eadaba') + path('M-27-1h50', 'none');
      break;
  }
  if (crypt) art = cryptPlace(p.id);
  else if(p.state==='current')art=path('M-57 0Q-18-13 25-7T59 6Q65 19 18 25T-58 17Z','#dabf79','stroke="none" opacity=".65"')
    +path('M-47 27Q-5 35 45 25','none','stroke="#a25b3d" stroke-width="2.4"')+art;
  // Sparse local hatching makes miniatures feel drawn, without obscuring their silhouette.
  const hatch = Array.from({ length: 4 }, () => { const x = rng.int(-27, 24), y = rng.int(14, 21); return path(`M${x} ${y}l5-2`, 'none', 'stroke-width=".8" opacity=".45"'); }).join('');
  return `<g class="atlas-place atlas-place--${p.state}" data-map-place="${String(p.id).replace(/[^\w:-]/g,'')}" transform="translate(${p.x} ${p.y - 8})" stroke="${ink}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${art}${hatch}</g>`;
}

function terrain(m: MapModel): string {
  const rng = new Rng(m.seed).stream('map:terrain'), ink = new Rng(m.seed).stream('map:ink');
  let art = '';
  if (m.mode === 'crypt') {
    art += path(m.route, 'none', 'stroke="#9b9681" stroke-width="46" stroke-linejoin="round"')
      + path(m.route, 'none', 'stroke="#e2d5b1" stroke-width="35" stroke-linejoin="round"');
    art += m.places.map(roomArt).join('');
  } else {
    for(const river of m.waterways){art+=path(riverShape(river,3),'#c3bf96','stroke="none" opacity=".55"')+path(riverShape(river),'#aec7b6','stroke="#8ea99a" stroke-width="1.2"');}
    // Forest groves occupy empty land and leave the riverbanks open.
    for (let k = 0; k < 11; k++) {
      const x = rng.int(35, W-35), y = rng.int(48, H-38);
      if (m.places.some(p => Math.hypot(p.x-x,(p.y-26)-y)<95)) continue;
      if(m.waterways.some(river=>river.some((p,i)=>i>0&&distanceToSegment({x,y},river[i-1],p)<48)))continue;
      art += path(blob(rng, x, y, rng.int(34,70), rng.int(21,36)), '#b6b895', 'opacity=".45" stroke="none"');
      for (let t = 0; t < 5; t++) art += tree(rng, x+rng.int(-24,24), y+rng.int(-9,19), .65+rng.float()*.35);
    }
    for (let i = 0; i < 36; i++) {
      const x=rng.int(28,W-25),y=rng.int(34,H-23);
      if (m.places.some(p=>Math.hypot(p.x-x,p.y-y)<66)) continue;
      art += path(`M${x} ${y}q7-5 14-2m-12 6q7-3 13-1`, 'none', 'stroke="#9a9871" stroke-width=".8" opacity=".55"');
    }
    // A northern ridge drawn as a contour and hatch marks, never a flat triangle banner.
    art += path('M278 47q16-18 34-5l19-17 27 21 24-13 32 17 23-24 30 19', 'none', 'stroke="#8b8968" stroke-width="1.5" opacity=".6"');
    for(let i=0;i<16;i++){const x=285+i*11;art+=path(`M${x} ${41+(i%3)*4}l-5 9`, 'none','stroke="#8b8968" stroke-width=".8" opacity=".45"');}
  }
  // Pen flecks and tiny fibres are deterministic and static.
  for (let i=0;i<95;i++) {const x=ink.int(12,W-12),y=ink.int(12,H-12);art+=path(`M${x} ${y}l${ink.int(1,4)} ${ink.int(-1,1)}`,'none','stroke="#8d7750" stroke-width=".6" opacity=".16"');}
  return art;
}

function svg(m: MapModel): string {
  const rng = new Rng(m.seed).stream('map:places');
  const stops = m.places.map(p => {
    const past = p.index < m.current.index || m.complete, current = p.index === m.current.index;
    return `<g transform="translate(${p.x} ${p.y+23})">${current?'<circle r="15" fill="none" stroke="#a25c40" stroke-width="1.5" opacity=".8"/>':''}<circle r="10" fill="${past?'#995443':current?'#e3bd71':'#efe0b7'}" stroke="${current?'#995443':'#816844'}" stroke-width="${current?'2.5':'1.6'}"/>`
      + (past ? path('M-4 0l3 3 5-6','none','stroke="#f4e5c1" stroke-width="2"') : `<text y="4" text-anchor="middle" fill="#675036" stroke="none" font-family="Georgia,serif" font-size="12">${p.index+1}</text>`)+ '</g>';
  }).join('');
  const compass = `<g transform="translate(647 357)" stroke="#887653" stroke-width="1.2" opacity=".75"><circle r="24" fill="none"/><circle r="18" fill="none"/><path d="M0-29L5-5 29 0 5 5 0 29-5 5-29 0-5-5Z" fill="#d3bf8b"/><path d="M0-29L0 0-5-5Z" fill="#925441"/><path d="M0 0L5 5 0 29Z" fill="#847553"/></g>`;
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" aria-hidden="true" focusable="false" data-map-seed="${m.seed.replace(/[^\w:-]/g,'')}">`
    + `<defs><radialGradient id="atlas-paper"><stop stop-color="#f5ebcc"/><stop offset="1" stop-color="#dfcea4"/></radialGradient></defs><rect width="${W}" height="${H}" fill="url(#atlas-paper)"/>`
    + path('M10 17Q170 7 350 14T708 14L710 392Q540 403 361 396T11 395Z','none','stroke="#b49a69" stroke-width="1"')
    + terrain(m)
    + path(m.route,'none',`stroke="${m.mode==='crypt'?'#9b855b':'#af9060'}" stroke-width="12" stroke-linecap="round" stroke-linejoin="round" opacity=".5"`)
    + path(m.route,'none','stroke="#f3e3bc" stroke-width="8" stroke-linecap="round" stroke-linejoin="round"')
    + path(m.route,'none','stroke="#8f7650" stroke-width="1.7" stroke-dasharray="2 7" stroke-linecap="round"')
    + (m.travelled?path(m.travelled,'none','stroke="#985a43" stroke-width="5" stroke-linecap="round" stroke-linejoin="round"')+path(m.travelled,'none','stroke="#e6b77c" stroke-width="1.2" stroke-linecap="round" stroke-dasharray="2 9"'):'')
    + m.places.map(p=>placeArt(p,m.mode==='crypt',rng.stream(p.id,p.index))).join('') + stops + compass + '</svg>';
}

function fallbackHero(): HTMLElement {
  const el=h('span',{class:'atlas-hero-fallback'});
  el.innerHTML='<svg viewBox="0 0 64 90" aria-hidden="true"><ellipse cx="32" cy="82" rx="20" ry="4" fill="#534732" opacity=".25"/><path d="M15 76L20 42H44L51 76Z" fill="#506d50" stroke="#443b2c" stroke-width="2"/><path d="M21 29L18 8 29 17 38 17 48 7 45 31Q43 44 32 44T21 29Z" fill="#bd985f" stroke="#443b2c" stroke-width="2"/><path d="M25 30h3m10 0h3M31 35l3 0M32 37v3M52 80V31" stroke="#443b2c" stroke-width="2.5"/><path d="M21 76v7m20-7v7" stroke="#443b2c" stroke-width="5"/></svg>';
  return el;
}

export function createJourneyMap(ctx: ScreenContext, journey: Journey, preview=false, previousStep?:number): { root:HTMLElement; dispose:()=>void } {
  const m=buildMapModel(journey,preview,previousStep), active=ctx.services.creatures.active();
  const placeName=(id:string)=>ctx.t(`explore.map.${m.mode==='crypt'?'cryptPlace':'place'}.${id}`);
  const stage=h('div',{class:`atlas-stage${m.mode==='crypt'?' atlas-stage--crypt':''}`,attrs:{'data-map-current':m.current.id}});
  stage.innerHTML=svg(m);
  const marker=heroPosition(m,m.current);
  const hero=h('div',{class:`atlas-hero${m.complete?' is-home':''}`,style:{left:`${marker.x/W*100}%`,top:`${marker.y/H*100}%`},attrs:{'aria-hidden':'true'}},fallbackHero());
  stage.append(hero);
  let portrait:ReturnType<NonNullable<typeof ctx.services.creatures.portrait>>|undefined, raf=0, stopped=false;
  if(active&&ctx.services.creatures.portrait){portrait=ctx.services.creatures.portrait(active.dna);hero.replaceChildren(portrait.canvas);hero.classList.add('has-portrait');}
  const motion=()=>!matchMedia('(prefers-reduced-motion: reduce)').matches&&!/^(reduced|min)$/.test(document.documentElement.dataset.motion??'');
  raf=requestAnimationFrame(start=>{
    if(stopped)return;
    if(m.previous&&motion()){
      const box=stage.getBoundingClientRect(),previous=heroPosition(m,m.previous),dx=(previous.x-marker.x)/W*box.width,dy=(previous.y-marker.y)/H*box.height;
      hero.animate([{translate:`${dx}px ${dy}px`},{translate:'0 0'}],{duration:320,easing:'cubic-bezier(.2,.7,.2,1)'});
    }
    const draw=(time:number)=>{if(stopped)return;portrait?.render(time);if(time-start<900)raf=requestAnimationFrame(draw);};draw(start);
  });
  const position=h('figcaption',{class:'atlas-caption'},
    h('span',{class:'atlas-caption-current'},h('i',{attrs:{'aria-hidden':'true'}},m.complete?'✓':'✦'),ctx.t(preview?'explore.map.preview':m.complete?'explore.map.arrived':journey.status!=='active'?'explore.map.stopped':'explore.map.here'),h('strong',null,placeName(m.current.id))),
    m.next?h('span',{class:'atlas-caption-next'},ctx.t('explore.map.next'),h('strong',null,placeName(m.next.id))):h('span',{class:'atlas-caption-next'},ctx.t('explore.map.destination')));
  const root=h('figure',{class:`road-atlas${preview?' is-preview':''}`,attrs:{'aria-label':ctx.t(m.mode==='crypt'?'explore.map.crypt':'explore.map.road')}},
    h('div',{class:'atlas-title'},h('span',null,ctx.t(m.mode==='crypt'?'explore.map.crypt':'explore.map.road')),h('span',null,ctx.t('explore.map.stops',{n:m.places.length}))),stage,position,
    h('details',{class:'atlas-route'},h('summary',null,ctx.t('explore.map.legend')),h('ol',null,m.places.map(p=>h('li',{class:`is-${p.state}`,attrs:{...(p.index===m.current.index?{'aria-current':'step'}:{})}},h('span',null,placeName(p.id)),h('small',null,ctx.t(m.complete||p.index<journey.step?'explore.map.visited':p.index===m.current.index?'explore.map.current':'explore.map.ahead')))))));
  return{root,dispose:()=>{stopped=true;cancelAnimationFrame(raf);portrait?.dispose();}};
}
